import * as THREE from 'three'
import { PBRTextures, getColorItem } from '~/utils/colorDictionary'
import {
  getCachedTexture,
  markTextureOwned,
  disposeMaterialOwnedTextures,
} from '../assets/textureCache'
import {
  getChromeRodMaterial,
  getSolidColorMaterial,
  isCachedMaterial,
} from './materialFactory'

const disposeIfNotCached = (material: THREE.Material): void => {
  if (isCachedMaterial(material)) return
  disposeMaterialOwnedTextures(material)
  material.dispose()
}

const disposeOldMaterials = (
  oldMaterials: (THREE.Material | THREE.Material[])[]
): void => {
  oldMaterials.forEach((entry) => {
    if (Array.isArray(entry)) {
      entry.forEach((mat) => {
        if (mat instanceof THREE.Material) disposeIfNotCached(mat)
      })
    } else if (entry instanceof THREE.Material) {
      disposeIfNotCached(entry)
    }
  })
}

export const applyColorToObject = (
  obj: THREE.Object3D,
  color: string
): void => {
  const solidColorMaterial = getSolidColorMaterial(color)

  obj.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return
    const mesh = o
    const previous = mesh.material

    mesh.material = solidColorMaterial
    mesh.material.needsUpdate = true

    disposeOldMaterials([previous])
  })
}

export const applyChromeRodMaterial = (obj: THREE.Object3D): void => {
  const chrome = getChromeRodMaterial()

  obj.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return
    const mesh = o
    const previous = mesh.material

    mesh.material = chrome
    mesh.material.needsUpdate = true
    mesh.castShadow = true
    mesh.receiveShadow = false

    disposeOldMaterials([previous])
  })
}

const applyColorTintToObject = (
  obj: THREE.Object3D,
  tintHex: string
): void => {
  const tint = new THREE.Color(tintHex)
  obj.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return

    // If a cached material was previously assigned (e.g. via a prior
    // solid-color recolor), tinting it in place would mutate every other
    // mesh sharing that cached material. Replace with an independent
    // clone first.
    const replaceIfCached = (mat: THREE.Material): THREE.Material => {
      if (!isCachedMaterial(mat)) return mat
      const fresh = mat.clone()
      fresh.userData.cached = false
      return fresh
    }

    if (Array.isArray(o.material)) {
      const replaced: THREE.Material[] = o.material.map(replaceIfCached)
      o.material = replaced
      replaced.forEach((mat) => {
        if (mat instanceof THREE.MeshStandardMaterial) {
          mat.color.copy(tint)
          mat.needsUpdate = true
        }
      })
    } else if (o.material instanceof THREE.Material) {
      o.material = replaceIfCached(o.material)
      if (o.material instanceof THREE.MeshStandardMaterial) {
        o.material.color.copy(tint)
        o.material.needsUpdate = true
      }
    }
  })
}

const applyPBRTexturesToObject = (
  obj: THREE.Object3D,
  pbrTextures: PBRTextures,
  rotateTexture: boolean = false
): void => {
  if (!pbrTextures.diffuse || !pbrTextures.normal || !pbrTextures.roughness) {
    console.warn(
      '⚠️ PBR textures missing required maps (diffuse, normal, roughness)'
    )
    return
  }
  const diffuseTexture = getCachedTexture(pbrTextures.diffuse)
  const normalTexture = getCachedTexture(pbrTextures.normal)
  const roughnessTexture = getCachedTexture(pbrTextures.roughness)
  const aoTexture = pbrTextures.aoRoughMetal
    ? getCachedTexture(pbrTextures.aoRoughMetal)
    : null
  const envMapTexture = pbrTextures.envMap
    ? getCachedTexture(pbrTextures.envMap)
    : null
  if (envMapTexture) {
    envMapTexture.mapping = THREE.EquirectangularReflectionMapping
  }

  if (!diffuseTexture || !normalTexture || !roughnessTexture) {
    console.warn('⚠️ Textures not preloaded! Call preloadPBRTextures() first')
    return
  }

  obj.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return
    const mesh = o
    const previous = mesh.material

    const hasWorldSpaceUVs = !!mesh.userData.worldSpaceUVs

    mesh.geometry.computeBoundingBox()
    const bbox = mesh.geometry.boundingBox
    if (bbox) {
      let repeatX = 1
      let repeatY = 1

      if (!hasWorldSpaceUVs) {
        const baseWidth = bbox.max.x - bbox.min.x
        const baseHeight = bbox.max.y - bbox.min.y
        const baseDepth = bbox.max.z - bbox.min.z

        mesh.updateMatrixWorld(true)
        const worldScale = new THREE.Vector3()
        mesh.getWorldScale(worldScale)

        const width = baseWidth * Math.abs(worldScale.x)
        const height = baseHeight * Math.abs(worldScale.y)
        const depth = baseDepth * Math.abs(worldScale.z)

        const areas = {
          xy: width * height,
          xz: width * depth,
          yz: height * depth,
        }

        const largestFace = Object.keys(areas).reduce((a, b) =>
          areas[a as keyof typeof areas] > areas[b as keyof typeof areas]
            ? a
            : b
        ) as keyof typeof areas

        let uDimension: number, vDimension: number
        if (largestFace === 'xy') {
          uDimension = width
          vDimension = height
        } else if (largestFace === 'xz') {
          uDimension = width
          vDimension = depth
        } else {
          uDimension = depth
          vDimension = height
        }

        const textureScaleCm = 50

        let finalUDimension = uDimension
        let finalVDimension = vDimension
        if (rotateTexture) {
          finalUDimension = vDimension
          finalVDimension = uDimension
        }

        repeatX = Math.max(0.5, finalUDimension / textureScaleCm)
        repeatY = Math.max(0.5, finalVDimension / textureScaleCm)
      }

      const meshDiffuse = markTextureOwned(diffuseTexture.clone())
      const meshNormal = markTextureOwned(normalTexture.clone())
      const meshRoughness = markTextureOwned(roughnessTexture.clone())
      const meshAO = aoTexture ? markTextureOwned(aoTexture.clone()) : null

      const wrapMode = hasWorldSpaceUVs
        ? THREE.ClampToEdgeWrapping
        : THREE.RepeatWrapping
      meshDiffuse.wrapS = meshDiffuse.wrapT = wrapMode
      meshNormal.wrapS = meshNormal.wrapT = wrapMode
      meshRoughness.wrapS = meshRoughness.wrapT = wrapMode
      if (meshAO) {
        meshAO.wrapS = meshAO.wrapT = wrapMode
      }

      if (rotateTexture && !hasWorldSpaceUVs) {
        meshDiffuse.rotation = Math.PI / 2
        meshNormal.rotation = Math.PI / 2
        meshRoughness.rotation = Math.PI / 2
        if (meshAO) meshAO.rotation = Math.PI / 2

        meshDiffuse.offset.set(0, 1)
        meshNormal.offset.set(0, 1)
        meshRoughness.offset.set(0, 1)
        if (meshAO) meshAO.offset.set(0, 1)
      }

      meshDiffuse.repeat.set(repeatX, repeatY)
      meshNormal.repeat.set(repeatX, repeatY)
      meshRoughness.repeat.set(repeatX, repeatY)
      if (meshAO) meshAO.repeat.set(repeatX, repeatY)

      meshDiffuse.needsUpdate = true
      meshNormal.needsUpdate = true
      meshRoughness.needsUpdate = true
      if (meshAO) meshAO.needsUpdate = true

      const baseColor = new THREE.Color(pbrTextures.colorTint || '#ffffff')
      const dimmingFactor = pbrTextures.dimmingFactor ?? 1.0
      baseColor.multiplyScalar(dimmingFactor)

      const materialParams: THREE.MeshStandardMaterialParameters = {
        map: meshDiffuse,
        color: baseColor,
        normalMap: meshNormal,
        normalScale: new THREE.Vector2(0.5, 0.5),
        roughnessMap: meshRoughness,
        roughness: 1.0,
        metalness: envMapTexture ? 0.15 : 0.0,
        envMapIntensity: envMapTexture ? 0.6 : 0,
      }
      if (envMapTexture) materialParams.envMap = envMapTexture
      if (meshAO) {
        materialParams.aoMap = meshAO
        materialParams.aoMapIntensity = 0.5
      }
      const pbrMaterial = new THREE.MeshStandardMaterial(materialParams)

      mesh.material = pbrMaterial
      mesh.material.needsUpdate = true
    } else {
      const baseColor = new THREE.Color(pbrTextures.colorTint || '#ffffff')
      const dimmingFactor = pbrTextures.dimmingFactor ?? 1.0
      baseColor.multiplyScalar(dimmingFactor)

      const fallbackDiffuse = markTextureOwned(diffuseTexture.clone())
      const fallbackNormal = markTextureOwned(normalTexture.clone())
      const fallbackRoughness = markTextureOwned(roughnessTexture.clone())
      const fallbackAO = aoTexture
        ? markTextureOwned(aoTexture.clone())
        : null

      const fallbackParams: THREE.MeshStandardMaterialParameters = {
        map: fallbackDiffuse,
        color: baseColor,
        normalMap: fallbackNormal,
        normalScale: new THREE.Vector2(0.5, 0.5),
        roughnessMap: fallbackRoughness,
        roughness: 1.0,
        metalness: envMapTexture ? 0.15 : 0.0,
        envMapIntensity: envMapTexture ? 0.6 : 0,
      }
      if (envMapTexture) fallbackParams.envMap = envMapTexture
      if (fallbackAO) {
        fallbackParams.aoMap = fallbackAO
        fallbackParams.aoMapIntensity = 0.5
      }
      const pbrMaterial = new THREE.MeshStandardMaterial(fallbackParams)

      mesh.material = pbrMaterial
      mesh.material.needsUpdate = true
    }

    disposeOldMaterials([previous])

    if (
      aoTexture &&
      mesh.geometry &&
      mesh.geometry.attributes.uv &&
      !mesh.geometry.attributes.uv2
    ) {
      mesh.geometry.setAttribute('uv2', mesh.geometry.attributes.uv)
    }
  })
}

export const applyMaterialToObject = (
  obj: THREE.Object3D,
  colorNameOrHex: string,
  rotateTexture: boolean = false
): void => {
  const colorItem = getColorItem(colorNameOrHex)

  if (colorItem?.tintColor) {
    applyColorTintToObject(obj, colorItem.tintColor)
  } else if (colorItem?.pbrTextures) {
    applyPBRTexturesToObject(obj, colorItem.pbrTextures, rotateTexture)
  } else {
    const hexColor = colorItem?.hexCode || colorNameOrHex
    applyColorToObject(obj, hexColor)
  }
}
